import { Fragment, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import heroDiamond from '../../assets/images/hero-diamond.png';
import cubeFaceSand from '../../assets/images/sand-hacks-cube-face-top.png';
import cubeFaceHacks from '../../assets/images/sand-hacks-cube-face-hacks.png';
import cubeFaceIcon from '../../assets/images/sand-hacks-cube-face-icon.png';
import seashell from '../../assets/images/seashell.png';
import { PoppableBubble } from '../FAQ/FAQ';
import bubble from '../../assets/images/bubble.png';
import hackathon1 from '../../assets/images/hackathon-1.jpeg';
import hackathon2 from '../../assets/images/hackathon-2.jpeg';
import hackathon3 from '../../assets/images/hackathon-3.jpeg';
import hackathon4 from '../../assets/images/hackathon-4.jpeg';
import hackathon5 from '../../assets/images/hackathon-5.jpeg';
import hackathon6 from '../../assets/images/hackathon-6.jpeg';
import seashellOutline from '../../assets/images/seashell-outline.png';
import starfishOutline from '../../assets/images/starfish-outline.png';
import tritonChatBubble from '../../assets/images/triton-chat-bubble.png';
import tritonPose from '../../assets/images/triton-pose-1.png';
import tritonPose2 from '../../assets/images/triton-pose-2.png';
import bubbleChain1 from '../../assets/images/bubble-chain-1.png';
import bubbleChain2 from '../../assets/images/bubble-chain-2.png';
import bubbleChain3 from '../../assets/images/bubble-chain-3.png';
import bubbleChain4 from '../../assets/images/bubble-chain-4.png';
import tritonInSub from '../../assets/images/triton-in-sub.png';
import tritonSwim from '../../assets/images/triton-swim-cutout.png';
import blueFish from '../../assets/images/blue-fish.png';
import purpleFish from '../../assets/images/purple-fish.png';
import blueJellyfish from '../../assets/images/blue-jellyfish.png';
import purpleJellyfish from '../../assets/images/purple-jellyfish.png';
import stingray from '../../assets/images/stingray.png';
import turtle from '../../assets/images/turtle-Photoroom.png';
import basementLogo from '../../assets/images/basement-logo.png';
import brightDataLogo from '../../assets/images/bright-data-logo.png';
import cseUcsdLogo from '../../assets/images/cse-ucsd-logo.png';
import figmaLogo from '../../assets/images/figma-logo.png';
import neonLogo from '../../assets/images/neon-logo.png';
import posthogLogo from '../../assets/images/posthog-logo.png';
import spaceXLogo from '../../assets/images/space-x-logo.png';
import sponsorBg from '../../assets/images/sponsor-bg.png';
import bubblePop1 from '../../assets/images/bubble-pop-1.png';
import bubblePop2 from '../../assets/images/bubble-pop-2.png';
import bubblePop3 from '../../assets/images/bubble-pop-3.png';
import bubblePop4 from '../../assets/images/bubble-pop-4.png';
import { colors, fonts, gradients } from '../../theme';
import FAQ from '../FAQ/FAQ';
import { SHOW_EVENT_SCHEDULE } from '../../config';

gsap.registerPlugin(ScrollTrigger);

const BUBBLE_POP_DURATION_MS = 110;
const BUBBLE_PAUSE_DURATION_MS = 400;

const aboutParagraph =
  "SanD Hacks is UC San Diego's premier student-run hackathon, bringing " +
  'together innovative minds to build, create, and collaborate over 48 hours. ' +
  "Whether you're a beginner or an experienced developer, you'll find your place here.";
const aboutWords = aboutParagraph.split(' ');

const impactStats = [
  { image: bubbleChain1, popImage: bubblePop1, value: '26', label: 'Hours of Coding', size: 290 },
  { image: bubbleChain2, popImage: bubblePop2, value: '150+', label: 'Participants', size: 250 },
  { image: bubbleChain3, popImage: bubblePop3, value: '35', label: 'Projects Built', size: 250 },
  { image: bubbleChain4, popImage: bubblePop4, value: '5000+', label: 'Git Commits', size: 300 },
];

// Event Schedule, one entry per day. On phones each day is a plain single column in `number`
// order (the array order); from `sm` up the cards are placed on a 3-column grid by `col`/`row`
// and nudged by `x`/`y` px so they ring the seashells at the centre of the grid. (`x`/`y` are
// the desktop values — they're scaled down at `sm`, see SCHEDULE_SM_SCALE.)
type ScheduleItem = {
  number: number;
  title: string;
  time: string;
  location?: string;
  col: number;
  row: number;
  x?: number;
  y?: number;
};

const JACOBS = 'Jacobs Hall (Outside Qualcomm Room)';
const QUALCOMM = 'Qualcomm Conference Room';

const scheduleDays: { label: string; staggerShells?: boolean; items: ScheduleItem[] }[] = [
  {
    label: 'Saturday, November 14',
    items: [
      { number: 1, title: 'Check-In', time: '9-10:00 AM', location: JACOBS, col: 1, row: 1 },
      { number: 2, title: 'Opening Ceremony', time: '10-10:30 AM', location: QUALCOMM, col: 2, row: 1, y: -100 },
      { number: 3, title: 'Start Hacking!', time: 'Starts 10:30 AM | Ends 10:00 PM', location: QUALCOMM, col: 3, row: 1, y: 8 },
      { number: 4, title: 'Team Formation Mixer', time: '10:30-11:30 AM', location: JACOBS, col: 1, row: 2 },
      { number: 5, title: 'Lunch', time: '12:30-1:30 PM', location: JACOBS, col: 2, row: 2, y: 100 },
      { number: 6, title: 'Dinner', time: '7:30-8:30 PM', location: JACOBS, col: 3, row: 2, y: -8 },
    ],
  },
  {
    // Reading order (left to right, top to bottom) around the shells in the empty centre cell.
    label: 'Sunday, November 15',
    staggerShells: true,
    items: [
      { number: 1, title: 'Breakfast', time: '7:30-8:30 AM', location: JACOBS, col: 1, row: 1 },
      { number: 2, title: 'Initial Submissions Due', time: '11:00 AM', location: QUALCOMM, col: 2, row: 1, y: -84 },
      { number: 3, title: 'Final Submissions Due', time: '12:00 PM', location: QUALCOMM, col: 3, row: 1 },
      { number: 4, title: 'Lunch', time: '12-12:30 PM', location: JACOBS, col: 1, row: 2, x: -56 },
      { number: 5, title: 'Presentations Begin', time: '12:30 PM', location: QUALCOMM, col: 3, row: 2, x: 56 },
      { number: 6, title: 'Presentations End', time: '2:30 PM', location: QUALCOMM, col: 1, row: 3 },
      { number: 7, title: 'Closing Ceremony', time: '2:30-3:00 PM', location: QUALCOMM, col: 2, row: 3, y: 108 },
      { number: 8, title: 'Event Concludes', time: '3:00 PM', col: 3, row: 3 },
    ],
  },
];

// The blue the Event Schedule section fades to by its bottom edge on desktop; the
// Schedule -> FAQ transition strip starts from the same colour so there's no seam.
const SCHEDULE_END_BLUE = '#3D49D2';

// How much of each card's `x`/`y` nudge applies at the `sm` breakpoint (full value from `md`).
const SCHEDULE_SM_SCALE = 0.6;
const SCHEDULE_SM_SCALE_X = 0.25;
const SCHEDULE_MD_SCALE_X = 0.6;

// Looping ambient motion for the Event Schedule decorations (keyframes live in App.css).
const drift = (duration: number, delay = 0) => ({
  animation: `scheduleDrift ${duration}s ease-in-out ${delay}s infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
const sway = (duration: number, delay = 0) => ({
  animation: `scheduleSway ${duration}s ease-in-out ${delay}s infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

// Ambient rise for the plain decorative background bubbles (the Hero/About scene + Event
// Schedule): loops continuously, starting below each bubble's resting spot and rising up past it
// while swaying side to side, fading out as it goes — like a real bubble working its way to the
// surface, then starting over. `seed`/`n` randomize each bubble's own rise/start/sway distances
// (same hash helper as the rest of the page's decorative layouts) so instances don't move in
// lockstep; `duration`/`delay` stagger the loop itself the same way `drift`/`sway` above do.
const bubbleRise = (seed: number, n: number, duration: number, delay = 0) => ({
  animation: `decorativeBubbleRise ${duration}s ease-in-out ${delay}s infinite`,
  '--rise': `${-(50 + hash(seed, n) * 70)}px`, // 50-120px of travel above the resting spot
  '--start': `${20 + hash(seed, n + 1) * 30}px`, // 20-50px below the resting spot at cycle start
  '--sway': `${6 + hash(seed, n + 2) * 10}px`, // 6-16px of side-to-side drift along the way
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

// Deterministic pseudo-random helper (same approach as FAQ.tsx) so decorative layouts are
// stable across renders instead of reshuffling via Math.random().
const hash = (seed: number, n: number) => {
  const x = Math.sin(seed * 12.9898 + n * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

// Warps a 0..1 value toward its own midpoint (0.5): power > 1 pulls values in from both ends
// toward the centre while still covering the full range, so a layout built from it reads as
// dense in the middle and sparse toward the edges instead of uniform.
const biasToCenter = (u: number, power: number) => {
  const s = u * 2 - 1; // 0..1 -> -1..1
  const biased = Math.sign(s) * Math.pow(Math.abs(s), power);
  return (biased + 1) / 2; // -1..1 -> 0..1
};

// The FAQ -> Sponsors transition: a stretch of open water, filled with bubbles that rise (and
// drift side to side) as the page is scrolled past them — tied to scroll position, not time, via
// the ScrollTrigger-driven effect below. This is the same span Triton's submarine already swims
// down, so resizing it resizes his swim too. Triton swims behind the bubble field (the field's
// zIndex sits above his), so he visually surfaces from behind them — and the field is deliberately
// concentrated toward the vertical middle of the strip (left/right stays an even spread) so that
// his appearance there reads as emerging from the thick of the bubbles rather than popping up
// next to them.
// Shared generator for both of the page's bubble-field transition strips (FAQ <-> Sponsors,
// Schedule <-> FAQ): dense, scroll-driven, vertically centre-biased, and left loose enough at
// top/bottom to drift past the strip's own edges into whatever section is adjacent. `topRange`
// is where that vertical range sits (percent of the strip's own height) — defaults to spanning
// the whole strip plus a bit of overflow on each side. `biasPower` controls how tightly bubbles
// cluster toward topRange's midpoint — lower values spread them out more evenly across the
// whole range (including its early/top end), higher values pull them in tighter.
const makeBubbleField = (
  seedBase: number,
  count: number,
  topRange: [number, number] = [-15, 115],
  biasPower = 2.4
) => {
  const [topMin, topMax] = topRange;
  return Array.from({ length: count }, (_, i) => {
    const rank = (i + 0.5) / count; // 0..1, evenly spaced
    const topU = biasToCenter(hash(seedBase, 100 + i), biasPower);
    return {
      left: rank * 100 + (hash(seedBase, i) - 0.5) * 4, // even spread left-to-right
      // Biased toward the middle of topRange, but loose enough the band reads wide rather than
      // a tight line.
      top: topMin + topU * (topMax - topMin),
      size: 14 + Math.pow(hash(seedBase, 200 + i), 1.5) * 56, // mostly small, a few big — 14-70px
      rise: 90 + hash(seedBase, 300 + i) * 200, // 90-290px of scroll-driven upward travel
      sway: (hash(seedBase, 400 + i) - 0.5) * 24,
      freq: 1 + hash(seedBase, 500 + i) * 2, // 1-3 side-to-side cycles over the scroll range
      // Each bubble fades in and back out around its own point in the scroll (not a shared
      // edge), so they disappear at scattered spots instead of along one uniform line. Unused
      // by fields that instead fade by position (see `fadeAbovePercent` below).
      center: 0.15 + hash(seedBase, 700 + i) * 0.7, // 0.15-0.85
      fadeWidth: 0.3 + hash(seedBase, 800 + i) * 0.25, // 0.3-0.55
    };
  });
};

// Sea life around the sponsors section. `z` layers them relative to Triton's sub (zIndex 1):
// 0 = behind him, 2 = in front of him but behind the seabed picture, 3 = above the seabed picture. `dx`/`dy` (vw) are the
// total travel across the scroll, `y` runs reversed for jellyfish so they drift upward while
// you scroll down. Percent positions are of the whole sponsors wrapper (heading + seabed).
// Fish face right, turtle and stingray face left, so each travels the way it faces.
const critters = [
  // Jellyfish: background, drift up with the scroll and bob up and down on their own.
  { src: purpleJellyfish, left: '4%', top: '14%', w: 9, z: 0, dx: 3, dy: -14, sway: 6, delay: 0, bob: true },
  { src: blueJellyfish, left: '86%', top: '10%', w: 7, z: 0, dx: -2, dy: -10, sway: 5, delay: 1.2, bob: true },
  { src: blueJellyfish, left: '46%', top: '36%', w: 5, z: 0, dx: 2, dy: -9, sway: 7, delay: 2.2, bob: true },
  // Big schools of fish, behind Triton, each spanning about half the page.
  { src: purpleFish, left: '48%', top: '22%', w: 50, z: 0, dx: 16, dy: 3, sway: 12, delay: 0.5 },
  { src: blueFish, left: '0%', top: '29%', w: 50, z: 0, dx: 18, dy: 3, sway: 10, delay: 1.6 },
  // Turtle and stingray swim in front of the seabed picture (zIndex 3 > its 2).
  { src: turtle, left: '60%', top: '46%', w: 24, z: 3, dx: -22, dy: 6, sway: 11, delay: 0.8 },
  { src: stingray, left: '4%', top: '54%', w: 28, z: 3, dx: -26, dy: 8, sway: 10, delay: 0.3 },
];

// Sponsor shells: each drops from the top of the screen as you scroll the seabed into view and
// comes to rest in a pile on the seabed floor. `left` is [mobile, desktop] percent of the page
// width, `bottom` is vw up from the bottom of the seabed, `tint` recolours the shell.
const sponsorShells = [
  { name: 'UC San Diego The Basement', logo: basementLogo, left: ['2%', '16%'], bottom: [8, 11], rot: -6, tint: 'none' },
  { name: 'Bright Data', logo: brightDataLogo, left: ['26%', '33%'], bottom: [7, 10], rot: 5, tint: 'hue-rotate(-35deg) saturate(1.1)' },
  { name: 'CSE UCSD', logo: cseUcsdLogo, left: ['50%', '50%'], bottom: [8, 11], rot: -4, tint: 'hue-rotate(160deg)' },
  { name: 'Figma', logo: figmaLogo, left: ['74%', '67%'], bottom: [7, 10], rot: 7, tint: 'hue-rotate(250deg)' },
  { name: 'Neon', logo: neonLogo, left: ['14%', '24.5%'], bottom: [18, 19], rot: 8, tint: 'hue-rotate(40deg) saturate(1.2)' },
  { name: 'PostHog', logo: posthogLogo, left: ['38%', '41.5%'], bottom: [19, 20], rot: -7, tint: 'hue-rotate(320deg)', logoScale: 1.5 },
  { name: 'SpaceX', logo: spaceXLogo, left: ['62%', '58.5%'], bottom: [18, 19], rot: 4, tint: 'hue-rotate(110deg)', logoScale: 1.5 },
];

const hackathonPhotos = [hackathon1, hackathon2, hackathon3, hackathon4, hackathon5, hackathon6];

const TRANSITION_HEIGHT = { xs: '14vh', md: '46vh' };
const transitionBubbles = makeBubbleField(777, 460);

// Same height and bubble count as the FAQ -> Sponsors field above, so the two fields actually
// match instead of one reading noticeably sparser than the other.
const SCHEDULE_FAQ_HEIGHT = TRANSITION_HEIGHT;
const scheduleFaqBubbles = makeBubbleField(555, 460);

type BubbleDatum = ReturnType<typeof makeBubbleField>[number];

// Wires a bubble field's rise/sway/fade to scroll position via one shared, scrubbed progress
// value, rather than a time-based loop — used by both the Schedule <-> FAQ and the FAQ <->
// Sponsors transition strips. Returns the gsap context's revert function for effect cleanup.
//
// By default each bubble fades in/out around its own randomized point in the scroll (scattered,
// not a shared edge). Passing `fadeAbovePercent` switches to a position-based fade instead: the
// bubble snaps up to fully visible over the last `fadeBand` percentage points before that line
// (percent of the strip's own height, matching a colour change in the strip's background) —
// quick, not gradual — and below that line it holds at `fadeFloor` (not zero), so bubbles
// positioned well past the line still show faintly rather than being invisible outright. That
// combination is what lets a field both "pop in" fast right at the colour change and still read
// as extending a good distance past it.
const setupBubbleScroll = (
  strip: HTMLElement,
  className: string,
  data: BubbleDatum[],
  opts?: { fadeAbovePercent?: number; fadeBand?: number; fadeFloor?: number; lead?: number }
) => {
  const ctx = gsap.context(() => {
    const els = gsap.utils.toArray<HTMLElement>(`.${className}`, strip);
    const progress = { p: 0 };
    const fadeBand = opts?.fadeBand ?? 14; // percentage points the position-based fade spans
    const fadeFloor = opts?.fadeFloor ?? 0; // opacity held below the fade band, instead of 0
    const apply = () => {
      const stripHeight = strip.offsetHeight || 1;
      els.forEach((el, i) => {
        const b = data[i];
        if (!b) return;
        const y = -b.rise * progress.p;
        const x = Math.sin(progress.p * Math.PI * 2 * b.freq) * b.sway;
        el.style.translate = `${x}px ${y}px`;

        let opacity: number;
        if (opts?.fadeAbovePercent != null) {
          const currentTopPercent = b.top + (y / stripHeight) * 100;
          const t = Math.min(1, Math.max(0, (currentTopPercent - (opts.fadeAbovePercent - fadeBand)) / fadeBand));
          opacity = fadeFloor + (1 - fadeFloor) * t;
        } else {
          // Each bubble fades in/out around its own `center` point in the scroll rather than a
          // shared edge, so they disappear at scattered spots along the way.
          opacity = Math.max(0, 1 - Math.abs(progress.p - b.center) / b.fadeWidth);
        }
        el.style.opacity = `${opacity}`;
      });
    };
    apply(); // sets correct initial state immediately, instead of a flash of full opacity

    gsap.to(progress, {
      p: 1,
      ease: 'none',
      scrollTrigger: { trigger: strip, start: `top bottom+=${opts?.lead ?? 0}`, end: 'bottom top', scrub: 0.4 },
      onUpdate: apply,
    });
  }, strip);

  return () => ctx.revert();
};

const Home = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [poppingIndex, setPoppingIndex] = useState<number | null>(null);
  const [popOffset, setPopOffset] = useState({ x: 0, y: 0, rotate: 0 });
  const popTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aboutTextRef = useRef<HTMLParagraphElement | null>(null);
  const scheduleRef = useRef<HTMLDivElement | null>(null);
  const swimRef = useRef<HTMLDivElement | null>(null);
  const sponsorsRef = useRef<HTMLDivElement | null>(null);
  const reefRef = useRef<HTMLDivElement | null>(null);
  const subRef = useRef<HTMLImageElement | null>(null);
  const transitionBubblesRef = useRef<HTMLDivElement | null>(null);
  const scheduleFaqBubblesRef = useRef<HTMLDivElement | null>(null);
  const swimmerRef = useRef<HTMLDivElement | null>(null);
  const impactSwimmerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const words = aboutTextRef.current?.querySelectorAll('.about-word');
    if (!words || words.length === 0) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        words,
        { opacity: 0.15, y: 14 },
        {
          opacity: 1,
          y: 0,
          ease: 'none',
          stagger: 0.04,
          scrollTrigger: {
            trigger: aboutTextRef.current,
            start: 'top 85%',
            end: 'bottom 55%',
            scrub: true,
          },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  // FAQ -> Sponsors transition bubbles: each one rises (and sways side to side) as the strip
  // scrolls through the viewport, tied to scroll position (see setupBubbleScroll above), not to
  // a time-based loop. Scrolling back up retraces the motion in reverse.
  useEffect(() => {
    const strip = transitionBubblesRef.current;
    if (!strip || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    return setupBubbleScroll(strip, 'transition-bubble', transitionBubbles);
  }, []);

  // Schedule -> FAQ transition bubbles: the same scroll-driven bubble field, between the
  // previous pair of sections.
  useEffect(() => {
    const strip = scheduleFaqBubblesRef.current;
    if (!strip || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // On phones the strip is short, so the bubbles get a head start (they begin rising before
    // the strip is on screen) and, via the wrapper's offset below, sit lower over the purple.
    const lead = window.matchMedia('(max-width: 899px)').matches ? 200 : 0;
    return setupBubbleScroll(strip, 'schedule-faq-bubble', scheduleFaqBubbles, { lead });
  }, []);

  // Sponsor shells: scroll-driven drop, all seven alike. As you scroll down the seabed each shell
  // falls from just above the top of the screen to its spot on the floor over its own slice of
  // the scroll (with a bounce and spin) and then stays there. Scrolling back up never lifts them;
  // they only reset once the whole sponsors section is off screen above you, so they drop again
  // on the next trip down.
  useEffect(() => {
    const swim = swimRef.current;
    const reef = reefRef.current;
    if (!swim || !reef || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      const shells = gsap.utils.toArray<HTMLElement>('.sponsor-shell');
      const bounce = gsap.parseEase('bounce.out');
      const slice = 0.4;
      const stagger = (1 - slice) / Math.max(shells.length - 1, 1);
      let maxP = 0;

      gsap.set(shells, { opacity: 0 });

      const place = (progress: number) => {
        const swimTop = swim.getBoundingClientRect().top + window.scrollY;
        shells.forEach((el, i) => {
          const q = gsap.utils.clamp(0, 1, (progress - i * stagger) / slice);
          const e = bounce(q);
          const restTop = swimTop + el.offsetTop;
          const startTop = window.scrollY - el.offsetHeight * 1.3;
          gsap.set(el, {
            y: (1 - e) * (startTop - restTop),
            x: Math.sin(q * Math.PI * 3) * (1 - q) * window.innerWidth * 0.02,
            rotation: Number(el.dataset.rot) + (1 - e) * (i % 2 ? 140 : -140),
            opacity: q > 0 ? 1 : 0,
          });
        });
      };

      ScrollTrigger.create({
        trigger: reef,
        start: 'top 70%',
        end: 'bottom bottom',
        onUpdate: (self) => {
          if (self.progress <= maxP) return; // only scrolling down moves them
          maxP = self.progress;
          place(maxP);
        },
      });

      // Reset only when the sponsors section is entirely below the viewport (scrolled back up).
      ScrollTrigger.create({
        trigger: swim,
        start: 'top bottom',
        onLeaveBack: () => {
          maxP = 0;
          gsap.set(shells, { opacity: 0 });
        },
      });
    }, swim);
    return () => ctx.revert();
  }, []);

  // Images (the seabed especially) load after the scroll triggers are measured, which on slower
  // phone connections leaves Triton's swim distance stale, so re-measure once everything's in.
  useEffect(() => {
    const refresh = () => ScrollTrigger.refresh();
    if (document.readyState === 'complete') refresh();
    else window.addEventListener('load', refresh);
    return () => window.removeEventListener('load', refresh);
  }, []);

  // Sea life: each animal travels its own distance as the sponsors wrapper scrolls through the
  // viewport (scrubbed, so it tracks the scrollbar both ways) — a parallax that sells depth.
  useEffect(() => {
    const swim = swimRef.current;
    if (!swim || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Phones get a plain looping CSS drift instead (see `critterTravel`), not scroll parallax.
    const mm = gsap.matchMedia();
    mm.add('(min-width: 900px)', () => {
      gsap.utils.toArray<HTMLElement>('.sponsor-critter').forEach((el) => {
        const dx = Number(el.dataset.dx);
        const dy = Number(el.dataset.dy);
        gsap.fromTo(
          el,
          { x: `${-dx / 2}vw`, y: `${-dy / 2}vw` },
          {
            x: `${dx / 2}vw`,
            y: `${dy / 2}vw`,
            ease: 'none',
            scrollTrigger: { trigger: swim, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
          }
        );
      });
    });
    return () => mm.revert();
  }, []);

  // Triton's submarine: starts swimming as the sponsors section begins, swims once across to the
  // right and back to the left on its way down, then dives behind the coral reef. Tied to scroll position (scrub),
  // and it always faces the way it is travelling, so scrolling back up flips it around and
  // retraces the path.
  useEffect(() => {
    const swim = swimRef.current;
    const sponsors = sponsorsRef.current;
    const reef = reefRef.current;
    const sub = subRef.current;
    if (!swim || !sponsors || !reef || !sub || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Heights are read at refresh time so the path follows the layout at any screen size.
    const sponsorsH = () => sponsors.offsetHeight;
    const reefTop = () => reef.offsetTop;
    const reefH = () => reef.offsetHeight;
    const endY = () => reefTop() + reefH() * 0.5; // deep enough to be hidden by the coral

    const ctx = gsap.context(() => {
      // One smooth sweep: x follows a single arc (start -> far right -> back left) while y
      // sinks at the scroll speed, so he tracks the centre of the screen.
      const place = (p: number) => {
        const vw = window.innerWidth / 100;
        // Page-y of the sub's top edge: scroll range length == travel distance, so it stays
        // level with the middle of the screen the whole way down.
        const y0 = -sub.offsetHeight / 2;
        return {
          x: (20 + 46 * Math.sin(Math.PI * p) - 8 * p) * vw,
          y: y0 + endY() * p,
        };
      };
      gsap.set(sub, { ...place(0), rotation: 10, scaleX: 1 });

      let facing = 1; // 1 = nose right, -1 = nose left
      let lastP = 0;
      const face = (dir: number) => {
        if (dir === facing) return;
        facing = dir;
        // Nose tips down in both directions (rotation is applied outside the flip).
        gsap.to(sub, { scaleX: dir, rotation: dir * 10, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      };

      const progress = { p: 0 };
      gsap.to(progress, {
        p: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: swim,
          start: 'top 50%',
          end: () => `top+=${endY()} 50%`,
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
        onUpdate: () => {
          const pos = place(progress.p);
          gsap.set(sub, pos);
          // Face the way he is actually travelling: the arc's slope at this point, flipped when
          // scrolling back up. (Going by the slope, not frame-to-frame x, avoids jitter flips.)
          const dp = progress.p - lastP;
          if (Math.abs(dp) > 1e-4) {
            const slope = 46 * Math.PI * Math.cos(Math.PI * progress.p) - 8;
            face(slope * dp > 0 ? 1 : -1);
            lastP = progress.p;
          }
        },
      });

      // Fades in later than before (a timed fade, not scrubbed) — he's already swimming by
      // this point (the position tween above starts at 'top 50%'), but stays invisible until
      // the bubble field has had a chance to build up, so he surfaces already surrounded by
      // bubbles instead of appearing into comparatively empty water.
      gsap.to(sub, {
        opacity: 1,
        duration: 0.25,
        scrollTrigger: { trigger: swim, start: 'top 20%', toggleActions: 'play none none reverse' },
      });
    }, swim);

    return () => ctx.revert();
  }, []);

  // Event Schedule: title, cards and number badges reveal as they scroll into view.
  useEffect(() => {
    const root = scheduleRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      // Each heading (the section title + one per day) reveals on its own trigger, so the
      // later days' headings don't play while they're still off-screen.
      gsap.utils.toArray<HTMLElement>('.schedule-title').forEach((title) => {
        gsap.from(title, {
          opacity: 0,
          y: 30,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: { trigger: title, start: 'top 88%' },
        });
      });

      gsap.utils.toArray<HTMLElement>('.schedule-item').forEach((item, i) => {
        const card = item.querySelector('.schedule-card');
        const badge = item.querySelector('.schedule-badge');
        // Hide up front so nothing flashes in before its trigger fires.
        gsap.set(card, { opacity: 0, y: 50 });
        gsap.set(badge, { scale: 0, rotation: -60 });

        gsap
          .timeline({ scrollTrigger: { trigger: item, start: 'top 90%' } })
          .to(card, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            delay: (i % 3) * 0.12, // stagger across each row of three
            ease: 'power3.out',
          })
          .to(badge, { scale: 1, rotation: 0, duration: 0.55, ease: 'back.out(2.2)' }, '-=0.35');
      });
    }, root);

    return () => ctx.revert();
  }, []);

  // Swimming Triton (desktop only): Triton swims around as the page scrolls. Everything is a
  // function of scroll position, rebuilt from the live layout on every ScrollTrigger refresh.
  // His position along each route is a pure function of one number — a scroll offset that
  // eases toward the real one — and he's always placed exactly ON the route for that number,
  // so scrolling back up retraces precisely the same path in reverse (the easing only ever
  // moves him along the route, never off it). Section by section:
  //
  //   About — he comes in from the right edge and swims down and across the photo seashells,
  //     turns one loop-the-loop on the way, and carries on off the left edge. Meanwhile the
  //     "2 days to create..." speech bubble pops in, and the posed Triton swims in from the
  //     right — lying sideways, then curving up into place beside it, where he stays.
  //   Our Impact — he comes back in from the left edge up by About's starfish, swims down to
  //     the stat bubbles and turns his loop BEHIND them, then swims on down into the Event
  //     Schedule, disappearing under that section's purple. This one is a second copy of him
  //     that lives inside the Our Impact section, layered under the bubbles and the schedule
  //     (the main one is fixed to the viewport, above everything, so can't go behind them).
  //   Event Schedule — off-screen while the cards scroll past; he crosses the gap between the
  //     two days, then makes a last pass under the final card and dives off the bottom of the
  //     screen before the FAQ. (The sponsors section has its own Triton, in the submarine.)
  //
  // Scrolling back up plays all of it in reverse, with him facing the way he's going.
  useEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 1200px) and (prefers-reduced-motion: no-preference)', () => {
      const el = swimmerRef.current;
      const impactEl = impactSwimmerRef.current;
      const shells = document.querySelector<HTMLElement>('.about-shells');
      const aboutPose = document.querySelector<HTMLElement>('.triton-pose-about');
      const aboutBubble = document.querySelector<HTMLElement>('.triton-bubble-about');
      const impactBubbles = document.querySelector<HTMLElement>('.impact-bubbles');
      const starfish = document.querySelector<HTMLElement>('.about-starfish');
      const schedule = scheduleRef.current;
      if (!el || !impactEl || !shells || !aboutPose || !aboutBubble || !impactBubbles || !starfish) return;

      // A "lap": points on the PAGE (x, page y), each reached at scroll offset `s`.
      type Lap = { s: number; x: number; y: number }[];
      // One swimmer following one lap. The About one is the viewport-fixed element; the Our
      // Impact one is positioned inside its section, whose page origin is (ox, oy).
      type Swim = {
        el: HTMLElement;
        fixed: boolean;
        lap: Lap;
        paced: boolean; // quick at the ends, slow in the middle (see lapAt)
        ox: number;
        oy: number;
        rot: number; // current target rotation, unwrapped
        rTo: (value: number, start?: number) => unknown;
      };
      const makeSwim = (node: HTMLElement, fixed: boolean): Swim => {
        gsap.set(node, { xPercent: -50, yPercent: -50 });
        return {
          el: node,
          fixed,
          lap: [],
          paced: fixed,
          ox: 0,
          oy: 0,
          rot: 0,
          rTo: gsap.quickTo(node, 'rotation', { duration: 0.5, ease: 'power2.out' }),
        };
      };
      const about = makeSwim(el, true);
      const impact = makeSwim(impactEl, false);

      // After the About lap the fixed swimmer follows keyframes in the VIEWPORT. Each flag
      // describes the leg that ENDS at that frame:
      //   track — swim sideways along a line of the page (y follows the scroll, x eases)
      //   warp  — he's off-screen and just needs to be somewhere else: hidden, no travel
      //   tilt  — end the leg rotated by this many degrees (instead of the sideways lean)
      type Frame = { s: number; x: number; y: number; track?: boolean; warp?: boolean; tilt?: number };
      let frames: Frame[] = [];
      let exit = { from: Infinity, to: Infinity }; // final fade-out, as he dives away
      let poseTravel = 0; // how far right the About pose starts, to be off-screen
      let cue = 0; // scroll offset at which About's bubble + pose start to arrive
      let reach = 0; // how far he extends from his centre, whichever way he's turned

      gsap.set(aboutBubble, { transformOrigin: '85% 90%' });

      const build = () => {
        // Measure the About pose where it really sits, not wherever it's been slid to.
        gsap.set(aboutPose, { x: 0, y: 0, rotation: 0 });

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const sy = window.scrollY;
        const w = el.offsetWidth;
        const h = w * (1287 / 664); // the swim image's aspect ratio
        const centre = (node: Element) => {
          const r = node.getBoundingClientRect();
          return { x: r.left + r.width / 2, y: r.top + sy + r.height / 2 };
        };
        const offLeft = -w;
        const offRight = vw + w;
        reach = h * 0.55;

        // A straight run on the page from `start` to `finish`, interrupted halfway by one
        // full circle: he pulls up, goes over the top and comes back round onto the same
        // line. (Sized so the whole circle, plus him, fits between the nav bar and the bottom
        // of the screen on shorter windows.) The scroll runs from "start is 74% down the
        // viewport" to "finish is 58% down" — both on screen, so you see him come in and see
        // him leave, and chosen so that the loop (which rises a full diameter above the line)
        // stays clear of the nav bar. Points are placed evenly along the path's length here;
        // lapAt() then bends that so he's quick at the two edges and slow through the middle.
        const radius = Math.min(130, (vh - 330) / 2.2);
        const makeLap = (start: { x: number; y: number }, finish: { x: number; y: number }): Lap => {
          const run = Math.hypot(finish.x - start.x, finish.y - start.y);
          const dir = { x: (finish.x - start.x) / run, y: (finish.y - start.y) / run };
          const side = dir.x < 0 ? 1 : -1; // which way round takes him UP and over
          const halfway = { x: (start.x + finish.x) / 2, y: (start.y + finish.y) / 2 };
          const hub = { x: halfway.x - side * dir.y * radius, y: halfway.y + side * dir.x * radius };
          const spoke = { x: halfway.x - hub.x, y: halfway.y - hub.y };
          const path = [start, halfway];
          for (let k = 1; k < 8; k++) {
            const turn = (side * k * Math.PI) / 4;
            path.push({
              x: hub.x + spoke.x * Math.cos(turn) - spoke.y * Math.sin(turn),
              y: hub.y + spoke.x * Math.sin(turn) + spoke.y * Math.cos(turn),
            });
          }
          path.push(halfway, finish);
          const from = start.y - vh * 0.74;
          const to = finish.y - vh * 0.58;
          let travelled = 0;
          const lengths = path.map((point, k) => {
            if (k > 0) travelled += Math.hypot(point.x - path[k - 1].x, point.y - path[k - 1].y);
            return travelled;
          });
          const lap = path.map((point, k) => ({ ...point, s: from + (to - from) * (lengths[k] / travelled) }));
          for (let k = 1; k < lap.length; k++) lap[k].s = Math.max(lap[k].s, lap[k - 1].s + 1);
          return lap;
        };

        // --- About: right to left across the photo seashells ---
        // Bounds of the shells themselves (the grid box also includes padding below them).
        let top = Infinity;
        let bottom = -Infinity;
        Array.from(shells.children).forEach((child) => {
          const r = child.getBoundingClientRect();
          top = Math.min(top, r.top + sy);
          bottom = Math.max(bottom, r.bottom + sy);
        });
        // From up beside the About text down to a little below the shells.
        about.lap = makeLap({ x: offRight, y: top - 250 }, { x: offLeft, y: bottom + 220 });
        const lapEnd = about.lap[about.lap.length - 1].s;
        // The speech bubble and posed Triton take their cue from the shells, not from the end
        // of his swim: he's still finishing off to the left as they arrive. (Which also means
        // that scrolling back up, he's already on his way in as they leave.)
        cue = bottom + 20 - vh * 0.55;
        poseTravel = vw - aboutPose.getBoundingClientRect().left + aboutPose.offsetHeight * 0.4;

        // --- Our Impact: in from the left, loop behind the stat bubbles, down into the schedule ---
        // Unlike the About lap this one isn't a straight line, so it's a list of anchors, each
        // with the scroll offset it's reached at — written as "this point is N% down the
        // viewport" — and the same full circle spliced in at the anchor marked `loop`.
        const row = centre(impactBubbles);
        const origin = (impactEl.offsetParent ?? impactEl).getBoundingClientRect();
        impact.ox = origin.left;
        impact.oy = origin.top + sy;
        const at = (x: number, y: number, frac: number) => ({ x, y, s: y - frac * vh });
        const anchors = [
          // Enters level with About's starfish, as it reaches the middle of the screen.
          at(offLeft, centre(starfish).y, 0.45),
          // The loop is centred on the row of bubbles, so it starts one radius below their
          // middle, while they're in the lower half of the screen.
          at(vw / 2, row.y + radius, 0.8),
        ];
        if (schedule) {
          // Then on down and into the schedule. That section sits above him and fades from
          // clear to solid purple over its first stretch, so he sinks out of sight into it.
          const scheduleTop = schedule.getBoundingClientRect().top + sy;
          anchors.push(at(vw * 0.72, scheduleTop + 30, 0.55), at(vw * 0.74, scheduleTop + 210, 0.62));
        } else {
          anchors.push(at(offRight, row.y + radius + 300, 0.58));
        }
        const loopScroll = vh * 0.42; // how much scrolling the circle itself takes
        const dir = (() => {
          const dx = anchors[2].x - anchors[0].x;
          const dy = anchors[2].y - anchors[0].y;
          const length = Math.hypot(dx, dy);
          return { x: dx / length, y: dy / length };
        })();
        const loopAt = anchors[1];
        // Heading right, so anticlockwise takes him up and over (the mirror of About's).
        const hub = { x: loopAt.x + dir.y * radius, y: loopAt.y - dir.x * radius };
        const spoke = { x: loopAt.x - hub.x, y: loopAt.y - hub.y };
        const route: Lap = [anchors[0], loopAt];
        for (let k = 1; k < 8; k++) {
          const turn = (-k * Math.PI) / 4;
          route.push({
            x: hub.x + spoke.x * Math.cos(turn) - spoke.y * Math.sin(turn),
            y: hub.y + spoke.x * Math.sin(turn) + spoke.y * Math.cos(turn),
            s: loopAt.s + (loopScroll * k) / 8,
          });
        }
        route.push({ ...loopAt, s: loopAt.s + loopScroll }, ...anchors.slice(2));
        for (let k = 1; k < route.length; k++) route[k].s = Math.max(route[k].s, route[k - 1].s + 1);
        impact.lap = route;

        // --- Event Schedule ---
        const list: Frame[] = [{ s: lapEnd, x: offLeft, y: about.lap[about.lap.length - 1].y - lapEnd }];
        exit = { from: lapEnd, to: lapEnd + 1 };
        if (schedule) {
          const end = schedule.getBoundingClientRect().top + sy + schedule.offsetHeight;
          const days = schedule.querySelectorAll('.schedule-title');
          if (days.length > 2) {
            // Right to left along the gap just above the second day's heading, while that
            // gap scrolls up through the viewport.
            const gap = centre(days[2]).y - 80;
            list.push(
              { s: gap - vh * 0.8, x: offRight, y: vh * 0.8, warp: true },
              { s: gap - vh * 0.2, x: offLeft, y: vh * 0.2, track: true },
            );
          }
          // Finale, in the open water under the last card: in from the left, then a
          // head-first dive off the bottom of the screen as the FAQ's bubbles rise.
          const floor = end - 90;
          list.push(
            { s: floor - vh * 0.9, x: offLeft, y: vh * 0.9, warp: true },
            { s: floor - vh * 0.5, x: vw * 0.46, y: vh * 0.5, track: true },
            { s: floor - vh * 0.15, x: vw * 0.56, y: vh + h, tilt: 150 },
          );
          exit = { from: floor - vh * 0.32, to: floor - vh * 0.15 };
        }

        // Keep the scroll offsets strictly increasing, whatever the layout threw up.
        for (let k = 1; k < list.length; k++) list[k].s = Math.max(list[k].s, list[k - 1].s + 1);
        frames = list;
      };

      const smooth = (t: number) => t * t * (3 - 2 * t);
      const clamp01 = (t: number) => Math.min(1, Math.max(0, t));

      // Where on the page a lap puts him at scroll offset `s`: a Catmull-Rom spline through
      // the lap's points, so the corners come out as curves.
      const lapAt = (lap: Lap, s: number, paced: boolean) => {
        const last = lap.length - 1;
        // Pacing (About's lap only): instead of a steady speed, 1.5x at either edge of the screen easing down to
        // 0.5x in the middle (where the loop is), so there's time to register that it's him.
        const whole = lap[last].s - lap[0].s;
        const t = Math.min(1, Math.max(0, (s - lap[0].s) / whole));
        const clamped = lap[0].s + whole * (paced ? t + (0.5 * Math.sin(2 * Math.PI * t)) / (2 * Math.PI) : t);
        let k = 0;
        while (k < last - 1 && clamped > lap[k + 1].s) k++;
        const u = (clamped - lap[k].s) / (lap[k + 1].s - lap[k].s);
        const p0 = lap[Math.max(0, k - 1)];
        const p1 = lap[k];
        const p2 = lap[k + 1];
        const p3 = lap[Math.min(last, k + 2)];
        const spline = (a: number, b: number, c: number, d: number) =>
          0.5 * (2 * b + (c - a) * u + (2 * a - 5 * b + 4 * c - d) * u * u + (3 * b - a - 3 * c + d) * u * u * u);
        return { x: spline(p0.x, p1.x, p2.x, p3.x), y: spline(p0.y, p1.y, p2.y, p3.y) };
      };

      // Place a swimmer at (x, y) — in its own coordinates, exactly, no easing — and turn it
      // toward `lean` the short way round from wherever it's currently pointing (headings wrap
      // at 360, and the loop takes him all the way round). Only the turn is eased.
      const steer = (swim: Swim, x: number, y: number, lean: number, jump: boolean) => {
        swim.rot += ((((lean - swim.rot) % 360) + 540) % 360) - 180;
        gsap.set(swim.el, { x, y });
        if (jump) {
          gsap.set(swim.el, { rotation: swim.rot });
          swim.rTo(swim.rot, swim.rot); // re-sync the tween to the jumped-to value
        } else {
          swim.rTo(swim.rot);
        }
      };

      // Put a swimmer where its lap says it is at scroll offset `s`.
      const followLap = (swim: Swim, s: number, direction: number, jump: boolean) => {
        const vh = window.innerHeight;
        const here = lapAt(swim.lap, s, swim.paced);
        // Never let the nav bar or the bottom of the screen cut him off mid-swim. (Part of the
        // route itself — it depends only on `s` — so it's the same in both directions.)
        const pageY = Math.min(s + vh - reach, Math.max(s + 75 + reach, here.y));
        // Head-first along the path, measured on the page — so he points down and across on
        // the way down, turns right over through the loop, and (the path running backwards)
        // up and back the other way when scrolling up.
        const ahead = lapAt(swim.lap, s + 6, swim.paced);
        const behind = lapAt(swim.lap, s - 6, swim.paced);
        const heading = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI + 90;
        steer(
          swim,
          here.x - swim.ox,
          // The fixed swimmer is placed in the viewport, so take off the REAL scroll offset:
          // that pins him to his spot on the page however far `s` is trailing it.
          swim.fixed ? pageY - window.scrollY : pageY - swim.oy,
          direction < 0 ? heading + 180 : heading,
          jump,
        );
      };

      const apply = (s: number, direction: number, instant = false) => {
        if (frames.length === 0 || about.lap.length === 0 || impact.lap.length === 0) return;
        const vh = window.innerHeight;

        // About's follow-up: the speech bubble pops in, then the posed Triton swims in from
        // the right edge — lying on his side, low, heading left — and curves up into his spot,
        // straightening as he arrives.
        const bubble = smooth(clamp01((s - (cue - vh * 0.03)) / (vh * 0.1)));
        gsap.set(aboutBubble, { opacity: bubble, scale: 0.6 + 0.4 * bubble });
        const landing = clamp01((s - (cue + vh * 0.02)) / (vh * 0.3));
        const across = smooth(clamp01(landing / 0.75)); // the sideways run in
        const rise = smooth(clamp01((landing - 0.35) / 0.65)); // then the swim up
        gsap.set(aboutPose, {
          x: poseTravel * (1 - across),
          y: 150 * (1 - rise),
          rotation: -78 * (1 - rise),
          opacity: landing > 0 ? 1 : 0,
        });

        // Our Impact's swimmer: only exists for the length of its own lap.
        const inImpact = s >= impact.lap[0].s && s <= impact.lap[impact.lap.length - 1].s;
        gsap.set(impact.el, { autoAlpha: inImpact ? 1 : 0 });
        followLap(impact, s, direction, instant || !inImpact);

        // The fixed swimmer: the About lap, then the schedule's keyframes.
        const lapEnd = about.lap[about.lap.length - 1].s;
        if (s <= lapEnd) {
          gsap.set(el, { autoAlpha: s < about.lap[0].s ? 0 : 1 });
          followLap(about, s, direction, instant);
          return;
        }
        if (frames.length < 2) {
          gsap.set(el, { autoAlpha: 0 });
          return;
        }
        let k = 0;
        while (k < frames.length - 2 && s > frames[k + 1].s) k++;
        const from = frames[k];
        const to = frames[k + 1];
        const raw = clamp01((s - from.s) / (to.s - from.s));
        const eased = smooth(raw);
        const x = to.warp ? to.x : from.x + (to.x - from.x) * eased;
        // (+ s - scrollY: same pinning to the page as in followLap.)
        const y =
          (to.warp ? to.y : from.y + (to.y - from.y) * (to.track ? raw : eased)) + s - window.scrollY;
        // Lean head-first into sideways travel — up to ~70deg, upright at each end of a leg.
        // Scrolling back up runs the leg in reverse, so he leans the other way.
        const dx = to.x - from.x;
        let lean = 0;
        if (to.tilt !== undefined) lean = to.tilt * eased;
        else if (!to.warp) {
          lean =
            Math.sign(dx) * 70 * Math.min(1, Math.abs(dx) / (window.innerWidth * 0.3)) *
            Math.sin(Math.PI * raw) * (direction < 0 ? -1 : 1);
        }
        const hidden = to.warp && raw < 1;
        const leaving = clamp01((s - exit.from) / (exit.to - exit.from));
        gsap.set(el, { autoAlpha: hidden ? 0 : 1 - leaving });
        steer(about, x, y, lean, instant || !!to.warp);
      };

      // `state.s` is the eased scroll offset everything above is drawn from; `facing` is which
      // way it's currently moving (so which way he points).
      const state = { s: 0 };
      let facing = 1;
      const trigger = ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => {
          const target = self.scroll();
          if (target !== state.s) facing = target > state.s ? 1 : -1;
          gsap.to(state, {
            s: target,
            duration: 0.45,
            ease: 'power2.out',
            overwrite: true,
            onUpdate: () => apply(state.s, facing),
          });
          // Redraw now too: the fixed swimmer has to move with every scroll event to stay
          // pinned to the page, even before the eased offset has budged.
          apply(state.s, facing);
        },
        onRefresh: (self) => {
          build();
          gsap.killTweensOf(state);
          state.s = self.scroll();
          apply(state.s, 1, true);
        },
      });
      build();
      state.s = trigger.scroll();
      apply(state.s, 1, true);

      return () => {
        trigger.kill();
        gsap.killTweensOf(state);
        gsap.set([aboutPose, aboutBubble], { clearProps: 'all' });
        gsap.set([el, impactEl], { autoAlpha: 0 });
      };
    });

    return () => mm.revert();
  }, []);

  const triggerPop = (i: number) => {
    if (popTimeoutRef.current) clearTimeout(popTimeoutRef.current);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    const angle = Math.random() * Math.PI * 2;
    const distance = 18 + Math.random() * 30;
    setPopOffset({
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      rotate: (Math.random() - 0.5) * 50,
    });
    setActiveIndex(i);
    setPoppingIndex(i);
    popTimeoutRef.current = setTimeout(() => {
      setPoppingIndex(null);
      popTimeoutRef.current = null;
      pauseTimeoutRef.current = setTimeout(() => {
        setActiveIndex(null);
        pauseTimeoutRef.current = null;
      }, BUBBLE_PAUSE_DURATION_MS);
    }, BUBBLE_POP_DURATION_MS);
  };

  return (
    <>
    {/* The swimming Triton — see the effect above. Hidden until that effect takes over (it
        only runs on desktop widths with motion allowed), so he never shows up un-driven. */}
    <Box
      ref={swimmerRef}
      aria-hidden
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: 'min(130px, 9vw)',
        zIndex: 30,
        pointerEvents: 'none',
        visibility: 'hidden',
        willChange: 'transform',
        display: { xs: 'none', lg: 'block' },
      }}
    >
      <Box
        component="img"
        src={tritonSwim}
        alt=""
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
          filter: 'drop-shadow(0 6px 10px rgba(2, 15, 93, 0.35))',
          animation: 'tritonPaddle 1.8s ease-in-out infinite',
        }}
      />
    </Box>
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'visible',
      }}
    >
      <Box
        component="img"
        src={heroDiamond}
        alt=""
        sx={{
          position: 'absolute',
          top: '59%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'max(100vw, 140vh)',
          height: 'auto',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', pt: { xs: 6, md: 10 }, px: 2 }}>
        <Typography
          sx={{
            fontFamily: fonts.heading,
            fontWeight: 500,
            fontSize: { xs: '1.25rem', md: '1.75rem', lg: '2.5rem' },
            color: colors.white,
          }}
        >
          UC San Diego Presents
        </Typography>
        <Typography
          sx={{
            fontFamily: fonts.display,
            fontWeight: 700,
            fontSize: { xs: '3rem', sm: '4.5rem', md: '7rem', lg: '10rem' },
            lineHeight: 1.1,
            color: colors.gold,
            textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
          }}
        >
          SanD Hacks
        </Typography>

        <Box
          role="img"
          aria-label="Spinning SanD Hacks cube"
          sx={{
            '--cube-size': 'min(220px, 32vw)',
            width: 'var(--cube-size)',
            height: 'var(--cube-size)',
            mt: { xs: 6, md: 9 },
            mb: { xs: 9, md: 13 },
            mx: 'auto',
            perspective: '900px',
          }}
        >
          <Box
            className="cube-spin-y"
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              transformStyle: 'preserve-3d',
              animation: 'cubeSpinY 32s linear infinite',
            }}
          >
            <Box
              className="cube-spin-x"
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                transformStyle: 'preserve-3d',
                animation: 'cubeSpinX 20s linear infinite',
              }}
            >
              {[
                { key: 'front', src: cubeFaceHacks, transform: 'rotateY(0deg) translateZ(calc(var(--cube-size) / 2))' },
                { key: 'back', src: cubeFaceHacks, transform: 'rotateY(180deg) translateZ(calc(var(--cube-size) / 2))' },
                { key: 'right', src: cubeFaceSand, transform: 'rotateY(90deg) translateZ(calc(var(--cube-size) / 2))' },
                { key: 'left', src: cubeFaceSand, transform: 'rotateY(-90deg) translateZ(calc(var(--cube-size) / 2))' },
                { key: 'top', src: cubeFaceIcon, transform: 'rotateX(90deg) translateZ(calc(var(--cube-size) / 2))' },
                { key: 'bottom', src: cubeFaceIcon, transform: 'rotateX(-90deg) translateZ(calc(var(--cube-size) / 2))' },
              ].map((face) => (
                <Box
                  key={face.key}
                  component="img"
                  src={face.src}
                  alt=""
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backfaceVisibility: 'hidden',
                    transform: face.transform,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: fonts.mono,
            fontWeight: 700,
            fontSize: { xs: '1.5rem', md: '2.25rem', lg: '2.75rem' },
            color: colors.white,
            mt: { xs: 4, md: 6 },
          }}
        >
          November 14-15, 2026
        </Typography>

        <Box
          component="a"
          href="https://docs.google.com/forms/d/e/1FAIpQLScs53tAUBWrcpVwE0nnHcP-pl1cv4yCS7sUNJzrgMIR-kjPmw/viewform?usp=sharing&ouid=110276353823843300021"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-block',
            textDecoration: 'none',
            backgroundColor: colors.gold,
            boxShadow: '1px 1px 7px #FFFFFF',
            borderRadius: '4px',
            padding: '10px 40px',
            mt: { xs: 5, md: 7 },
            animation: 'ctaPulse 2.4s ease-in-out infinite',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
            '&:hover': {
              transform: 'scale(1.07)',
              animation: 'none',
              boxShadow: `0 0 14px rgba(235, 177, 17, 0.65)`,
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: fonts.mono,
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '1.75rem' },
              color: colors.white,
            }}
          >
            Apply Now
          </Typography>
        </Box>
      </Box>
    </Box>

    <Box
      component="section"
      id="about"
      sx={{
        textAlign: 'center',
        px: 2,
        pt: { xs: 16, md: 24 },
        pb: { xs: 8, md: 12 },
        maxWidth: '900px',
        mx: 'auto',
      }}
    >
      <Typography
        sx={{
          fontFamily: fonts.display,
          fontWeight: 400,
          fontSize: { xs: '2rem', sm: '3rem', md: '4.5rem' },
          color: colors.gold,
          textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
          mb: 4,
        }}
      >
        What is SanD Hacks?
      </Typography>
      <Typography
        ref={aboutTextRef}
        sx={{
          fontFamily: fonts.body,
          fontWeight: 700,
          fontSize: { xs: '1.1rem', md: '1.5rem' },
          lineHeight: 1.6,
          color: colors.white,
        }}
      >
        {aboutWords.map((word, i) => (
          <Box
            key={i}
            component="span"
            className="about-word"
            sx={{ display: 'inline-block', willChange: 'opacity, transform' }}
          >
            {word}
            {i < aboutWords.length - 1 ? ' ' : ''}
          </Box>
        ))}
      </Typography>
    </Box>

    <Box sx={{ position: 'relative' }}>
      <PoppableBubble
        display={{ xs: 'none', sm: 'block' }}
        sx={{ left: { xs: '2%', md: '6%' }, top: '28%', width: 'min(90px, 8vw)', }}
        rise={bubbleRise(601, 0, 6.5, 0)}
      />
      <PoppableBubble
        display={{ xs: 'none', sm: 'block' }}
        sx={{ left: { xs: '0%', md: '3%' }, top: '58%', width: 'min(50px, 5vw)', }}
        rise={bubbleRise(601, 10, 5.5, 1.6)}
      />
      <Box
        component="img"
        src={seashellOutline}
        alt=""
        sx={{
          position: 'absolute',
          // The PNG has a wide transparent margin on its left, hence the negative offset. Capped
          // in px so that on very wide screens (where the image stops growing but a % offset
          // keeps growing) the shell itself doesn't slide off the left edge of the page.
          left: { xs: '-16%', md: 'max(-14%, -180px)' },
          top: '65%',
          width: 'min(380px, 34vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={seashellOutline}
        alt=""
        sx={{
          position: 'absolute',
          left: { xs: '16%', md: '19%' },
          top: '95%',
          width: 'min(220px, 19vw)',
          height: 'auto',
          transform: 'scaleX(-1)',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={starfishOutline}
        alt=""
        className="about-starfish"
        sx={{
          position: 'absolute',
          left: { xs: '2%', md: '5%' },
          top: '127%',
          width: 'min(400px, 35vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <PoppableBubble
        display={{ xs: 'none', sm: 'block' }}
        sx={{ right: { xs: '4%', md: '7%' }, top: '75%', width: 'min(60px, 5.5vw)', }}
        rise={bubbleRise(601, 20, 7, 0.8)}
      />
      <PoppableBubble
        display={{ xs: 'none', sm: 'block' }}
        sx={{ right: { xs: '10%', md: '14%' }, top: '86%', width: 'min(95px, 8.5vw)', }}
        rise={bubbleRise(601, 30, 6, 2.4)}
      />
      <Box
        className="triton-bubble-about"
        sx={{
          position: 'absolute',
          right: { xs: '20%', md: '25%' },
          top: '90%',
          width: 'min(510px, 34vw)',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        <Box
          component="img"
          src={tritonChatBubble}
          alt=""
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <Typography
          sx={{
            position: 'absolute',
            top: '36%',
            left: '18%',
            width: '72%',
            fontFamily: fonts.body,
            fontWeight: 700,
            // Scales with the bubble image (which is sized in vw) so the line always fits inside it.
            fontSize: 'clamp(0.7rem, 1.9vw, 1.5rem)',
            lineHeight: 1.3,
            color: colors.white,
            textAlign: 'left',
          }}
        >
          2 days to create something amazing!
        </Typography>
      </Box>
      <Box
        component="img"
        src={tritonPose}
        alt="Triton mascot"
        className="triton-pose-about"
        sx={{
          position: 'absolute',
          right: { xs: '8%', md: '10%' },
          top: '112%',
          width: 'min(260px, 22vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <Box
        component="section"
        className="about-shells"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, auto)', sm: 'repeat(3, auto)' },
          justifyContent: 'center',
          columnGap: { xs: 2, sm: 1 },
          rowGap: { xs: 2, md: 1 },
          justifyItems: 'center',
          maxWidth: '1000px',
          mx: 'auto',
          px: 2,
          mt: { xs: -3, md: -5 },
          pb: { xs: 6, md: 10 },
        }}
      >
        {[-8, 6, -4, 5, -6, 9].map((rotation, i) => (
          <Box
            key={i}
            sx={{
              animation: `seashellFloat ${4.5 + (i % 3) * 0.6}s ease-in-out ${i * 0.35}s infinite`,
              '&:hover': { animationPlayState: 'paused' },
            }}
          >
            {/* The photo sits on the shell and turns/scales with it. */}
            <Box
              sx={{
                position: 'relative',
                // Three across from `sm` up, so each shell gets just under a third of the width.
                width: { xs: 'min(200px, 42vw)', sm: 'min(260px, 29vw)' },
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.35s ease, filter 0.35s ease',
                '&:hover': {
                  transform: `rotate(${rotation}deg) scale(1.1)`,
                  filter: `drop-shadow(0 0 14px ${colors.gold})`,
                },
              }}
            >
              <Box component="img" src={seashell} alt="" sx={{ width: '100%', height: 'auto', display: 'block' }} />
              <Box
                component="img"
                src={hackathonPhotos[i]}
                alt=""
                sx={{
                  position: 'absolute',
                  left: '23%',
                  top: '19%',
                  width: '54%',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Mobile only: Triton mascot + speech bubble + decorative icons, stacked below the seashells */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'flex-end',
          position: 'relative',
          mt: -6,
          pr: { xs: 2, sm: 4 },
          pb: { xs: 4, sm: 12 },
        }}
      >
        <PoppableBubble
          sx={{ left: '6%', top: '-8px', width: '36px' }}
          rise={bubbleRise(601, 40, 6, 0.3)}
          display="block"
        />
        <Box
          component="img"
          src={seashellOutline}
          alt=""
          sx={{
            position: 'absolute',
            left: '-6%',
            top: '55%',
            width: 'min(140px, 34vw)',
            height: 'auto',
          }}
        />
        <Box
          component="img"
          src={starfishOutline}
          alt=""
          sx={{
            position: 'absolute',
            left: '10%',
            top: '88%',
            width: 'min(120px, 30vw)',
            height: 'auto',
            opacity: 0.9,
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
          <Box sx={{ position: 'relative', width: 'min(230px, 60vw)', flexShrink: 0 }}>
            <Box
              component="img"
              src={tritonChatBubble}
              alt=""
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
            <Typography
              sx={{
                position: 'absolute',
                top: '32%',
                left: '17%',
                width: '72%',
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: '0.72rem',
                lineHeight: 1.25,
                color: colors.white,
                textAlign: 'left',
              }}
            >
              2 days to create something amazing!
            </Typography>
          </Box>

          <Box
            component="img"
            src={tritonPose}
            alt="Triton mascot"
            sx={{
              width: 'min(130px, 34vw)',
              height: 'auto',
              flexShrink: 0,
              ml: '-26px',
            }}
          />
        </Box>
      </Box>
    </Box>

    {/* Reserves room below for the rest of the page gradient
        (purple/indigo/deep-blue) until more content sections are built here. */}
    <Box sx={{ minHeight: { xs: 0, sm: '28vh', md: '47vh' } }} />
    <Box
      id="impact"
      sx={{
        position: 'relative',
        minHeight: { xs: '58vh', sm: '95vh', md: '120vh' },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: gradients.impact,
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
        }}
      />

      <Box sx={{ position: 'relative', textAlign: 'center', width: '100%', mt: { xs: -10, md: -14 }, pt: { xs: 8, md: 12 }, px: 2 }}>
        {/* Our Impact's own swimming Triton (see the swim effect above). First in this box and
            zIndex 0, so every stat bubble — positioned, and later in the DOM — paints over
            him: he swims BEHIND the bubbles and shows through their glass. */}
        <Box
          ref={impactSwimmerRef}
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: 'min(130px, 9vw)',
            zIndex: 0,
            pointerEvents: 'none',
            visibility: 'hidden',
            willChange: 'transform',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Box
            component="img"
            src={tritonSwim}
            alt=""
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              animation: 'tritonPaddle 1.8s ease-in-out infinite',
            }}
          />
        </Box>
        <Typography
          sx={{
            fontFamily: fonts.display,
            fontWeight: 700,
            fontSize: { xs: '2.5rem', sm: '4rem', md: '5.5rem' },
            color: colors.gold,
            textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
            mb: { xs: 3, md: 4 },
          }}
        >
          Our Impact
        </Typography>

        <Box
          className="impact-bubbles"
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            justifyContent: 'center',
            alignItems: 'center',
            width: { xs: 'min(250px, 72vw)', md: 'auto' },
            mx: 'auto',
            px: { xs: 0, md: 0 },
          }}
        >
          {impactStats.map((stat, i) => (
            <Box
              key={stat.label}
              onClick={() => triggerPop(i)}
              sx={{
                position: 'relative',
                flexShrink: 0,
                cursor: 'pointer',
                width: { xs: stat.size * 0.48, md: stat.size },
                height: {
                  xs: stat.size * 0.48 * (i === 3 ? 1.15 : 1),
                  md: stat.size * (i === 3 ? 1.1 : 1),
                },
                m: { xs: '-10px', md: 0 },
                ml: { md: i === 0 ? 0 : '-30px' },
                zIndex: activeIndex === i ? 20 : i === 1 ? 10 : i,
                transformOrigin: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.12)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  transformOrigin: 'center',
                  transition: activeIndex === i
                    ? 'transform 0.1s ease, opacity 0.1s ease'
                    : 'transform 0.75s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease',
                  transform: activeIndex === i ? 'scale(0.3)' : 'scale(1)',
                  opacity: activeIndex === i ? 0 : 1,
                }}
              >
                <Box
                  component="img"
                  src={stat.image}
                  alt=""
                  sx={{ width: '100%', height: '100%', display: 'block' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 1, md: 2 },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fonts.mono,
                      fontWeight: 900,
                      fontSize: { xs: '1.35rem', md: '3.1rem' },
                      color: '#FFC300',
                      textShadow: '0 0 10px rgba(255, 195, 0, 0.55)',
                      lineHeight: 1.1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fonts.mono,
                      fontWeight: 400,
                      fontSize: { xs: '0.56rem', md: '1.15rem' },
                      color: colors.white,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
              <Box
                component="img"
                src={stat.popImage}
                alt=""
                sx={{
                  position: 'absolute',
                  top: '-10%',
                  left: '-10%',
                  width: '120%',
                  height: '120%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  transformOrigin: 'center',
                  transition: poppingIndex === i
                    ? 'transform 0.14s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.1s ease'
                    : 'transform 0.15s ease, opacity 0.15s ease',
                  transform: poppingIndex === i
                    ? `translate(${popOffset.x}px, ${popOffset.y}px) scale(1) rotate(${popOffset.rotate}deg)`
                    : 'translate(0, 0) scale(0.4) rotate(0deg)',
                  opacity: poppingIndex === i ? 1 : 0,
                }}
              />
            </Box>
          ))}
        </Box>

        <Box
          component="img"
          src={tritonPose2}
          alt="King Triton mascot"
          sx={{
            position: 'absolute',
            left: { xs: '-1%', md: '3%' },
            top: { xs: '96%', md: '96%' },
            width: { xs: 'min(150px, 30vw)', md: 'min(220px, 16vw)' },
            height: 'auto',
            zIndex: 15,
            filter: `drop-shadow(0 0 5px ${colors.gold}) drop-shadow(0 0 10px ${colors.gold})`,
          }}
        />
      </Box>
    </Box>

    {SHOW_EVENT_SCHEDULE && (
    <>
    <Box
      component="section"
      id="schedule"
      ref={scheduleRef}
      sx={{
        position: 'relative',
        pt: { xs: 10, md: 14 },
        // Room below the last day's grid for its bottom-centre card (which hangs below the
        // grid) and the starfish beside it. The section clips its own overflow (below), so
        // this has to be tall enough to actually contain them.
        // (Desktop includes the gap that used to be a margin above the transition strip — it has
        // to be this section's own padding now, so its blue runs unbroken into the strip.)
        pb: { xs: 4, sm: 26, md: 42 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
        isolation: 'isolate',
      }}
    >
      {/* The section paints its own colour hand-off: picks up the page's purple just below
          its top edge, then eases toward blue down its length — into the FAQ's periwinkle on
          phones, and into a deeper blue on desktop (which the Schedule -> FAQ strip below
          then carries on from). */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          background: {
            xs: 'linear-gradient(180deg, rgba(123, 49, 150, 0) 0%, #7B3196 12%, #88A3EA 100%)',
            md: `linear-gradient(180deg, rgba(123, 49, 150, 0) 0%, #7B3196 6%, #7B3196 16%, ${SCHEDULE_END_BLUE} 100%)`,
          },
        }}
      />
      {/* Decorative bubbles */}
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          left: '4%',
          top: '10%',
          ...bubbleRise(602, 0, 6, 0),
          width: 'min(90px, 7vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          left: '2%',
          top: '15%',
          ...bubbleRise(602, 10, 5, 1.2),
          width: 'min(45px, 3.5vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          right: '2%',
          top: '19%',
          ...bubbleRise(602, 20, 7, 0.6),
          width: 'min(92px, 7vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          right: '4%',
          top: '25%',
          ...bubbleRise(602, 30, 5.5, 2),
          width: 'min(55px, 4.5vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          left: '8%',
          top: '49%',
          ...bubbleRise(602, 40, 5, 0.9),
          width: 'min(50px, 4vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          left: '1.5%',
          top: '53%',
          ...bubbleRise(602, 50, 6.5, 0.3),
          width: 'min(92px, 7vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          right: '2%',
          top: '81%',
          ...bubbleRise(602, 60, 6, 1.6),
          width: 'min(92px, 7vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Box
        component="img"
        src={bubble}
        alt=""
        sx={{
          position: 'absolute',
          right: '7%',
          top: '86%',
          ...bubbleRise(602, 70, 5, 0.2),
          width: 'min(50px, 4vw)',
          height: 'auto',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <Typography
        className="schedule-title"
        sx={{
          fontFamily: fonts.display,
          fontWeight: 700,
          fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
          color: colors.gold,
          textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
          textAlign: 'center',
          mb: { xs: 6, md: 8 },
        }}
      >
        Event Schedule
      </Typography>

      {scheduleDays.map((day, dayIndex) => {
        // How far this day's cards poke above / hang below the grid's own box (desktop px),
        // so the heading above and whatever follows below can leave room for them.
        const rise = Math.max(0, ...day.items.map((item) => -(item.y ?? 0)));
        // (Previous day's overhang goes into this heading's top margin rather than a bottom
        // margin on that day's wrapper — the two margins would just collapse into one.)
        const prev = scheduleDays[dayIndex - 1];
        const prevSink = prev ? Math.max(0, ...prev.items.map((item) => item.y ?? 0)) : 0;
        return (
        <Fragment key={day.label}>
      <Typography
        className="schedule-title"
        sx={{
          fontFamily: fonts.heading,
          fontWeight: 700,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          color: '#B9E5EE',
          textShadow: '0px 3px 4px rgba(0, 0, 0, 0.3)',
          textAlign: 'center',
          mt: dayIndex === 0
            ? 0
            : { xs: 8, sm: `${prevSink * SCHEDULE_SM_SCALE + 90}px`, md: `${prevSink + 110}px` },
          mb: {
            xs: '32px',
            sm: `${rise * SCHEDULE_SM_SCALE + 44}px`,
            md: `${rise + 50}px`,
          },
        }}
      >
        {day.label}
      </Typography>

      {/* Wraps the grid plus the decorations hanging off it, so those decorations can anchor
          to "the grid's bottom edge" (top: 100% of this wrapper) instead of a percentage of
          the whole section. This wrapper is full-width (no maxWidth), same as the section
          itself, so the decorations' `left`/`right` offsets measure from the page edges. */}
      <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          maxWidth: '1000px',
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          columnGap: { sm: 2, md: 3.5 },
          rowGap: { xs: 3, sm: 4, md: 5 },
          // Above the grey outline shells/starfish that hang off this grid (they come later
          // in the DOM, so would otherwise paint over the cards): if a card ever reaches one
          // — a hover lift, a mid-sway starfish, an odd screen width — the card covers it.
          zIndex: 1,
        }}
      >
        {day.items.map((item) => {
          const glow = item.number % 2 === 1 ? colors.gold : '#ADE1FF';
          const x = item.x ?? 0;
          const y = item.y ?? 0;
          return (
          <Box
            key={item.number}
            className="schedule-item"
            sx={{
              position: 'relative',
              gridColumn: { sm: item.col },
              gridRow: { sm: item.row },
              transform: {
                // Sideways nudges shrink further than vertical ones at `sm`: the grid already
                // runs close to the screen edges there, so there's less room to push outward.
                sm: `translate(${x * SCHEDULE_SM_SCALE_X}px, ${y * SCHEDULE_SM_SCALE}px)`,
                // Sideways nudges only get their full value once the grid has real margins.
                md: `translate(${x * SCHEDULE_MD_SCALE_X}px, ${y}px)`,
                lg: `translate(${x}px, ${y}px)`,
              },
              // Hover reacts on the whole item (card + its number badge).
              // The lift/scale goes on `.schedule-lift`, a wrapper GSAP never animates:
              // GSAP writes `translate: none; scale: none` inline on the elements it
              // touches (the card and badge), which would override these rules there.
              // Hovering this static item, not the lifted layer, avoids edge flicker.
              '@media (hover: hover) and (prefers-reduced-motion: no-preference)': {
                '&:hover .schedule-lift': {
                  translate: '0 -8px',
                  scale: '1.03',
                },
                '&:hover .schedule-card': {
                  boxShadow: `0 0 24px 6px ${glow}B3, 0 16px 30px rgba(0, 0, 0, 0.35)`,
                },
                '&:hover .schedule-card::after': {
                  animation: 'scheduleShine 0.9s ease',
                },
                '&:hover .schedule-badge': {
                  animation: 'scheduleWiggle 0.6s ease',
                },
              },
            }}
          >
            <Box
              className="schedule-lift"
              sx={{
                position: 'relative',
                transition: 'translate 0.25s ease, scale 0.25s ease',
              }}
            >
            <Box
              className="schedule-badge"
              sx={{
                position: 'absolute',
                // Sits inside the card's top-left corner, above the title.
                top: 18,
                left: { xs: 18, sm: 10, md: 18 },
                width: '58px',
                height: '58px',
                zIndex: 2,
              }}
            >
              {/* Navy core — tinted to indigo by the translucent shell above it */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '6px',
                  top: '6px',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#020F5D',
                }}
              />
              {/* Glass bubble shell */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: 'rgba(201, 247, 255, 0.2)',
                  border: '5px solid rgba(102, 205, 228, 0.43)',
                  boxShadow:
                    '-1px 4px 8.4px -1px rgba(0, 0, 0, 0.25), inset 12px 9px 6.7px -2px #B9E5EE',
                }}
              />
              {/* Shine crescents + dots, mirrored top and bottom */}
              <Box
                component="svg"
                viewBox="0 0 58 58"
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              >
                <g fill="#FFFFFF" transform="translate(0 3)">
                  <path transform="rotate(-26 25.25 10.1) translate(52 0) scale(-1 1)" d="M21.2 10.4 C 23.6 8.7, 27.8 7.6, 32.3 7.8 C 27.6 9.2, 24.4 10.7, 22.7 12.6 Z" />
                  <ellipse cx="34.6" cy="9.0" rx="2.1" ry="1.45" transform="rotate(16 34.6 9)" />
                </g>
                <g fill="#FFFFFF" transform="translate(0 -3) rotate(180 30 30)">
                  <path transform="rotate(-26 25.25 10.1) translate(52 0) scale(-1 1)" d="M21.2 10.4 C 23.6 8.7, 27.8 7.6, 32.3 7.8 C 27.6 9.2, 24.4 10.7, 22.7 12.6 Z" />
                  <ellipse cx="34.6" cy="9.0" rx="2.1" ry="1.45" transform="rotate(16 34.6 9)" />
                </g>
              </Box>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '6px',
                  top: '6px',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: fonts.ui,
                  fontWeight: 700,
                  fontSize: '24px',
                  lineHeight: 1,
                  color: '#FFFFFF',
                }}
              >
                {item.number}
              </Typography>
            </Box>

            <Box
              className="schedule-card"
              sx={{
                position: 'relative',
                backgroundColor: '#FDF6E3',
                border: `2px solid ${colors.gold}`,
                borderRadius: '12px',
                p: { xs: 3, sm: 2, md: 3 },
                // Clears the number badge sitting in the top-left corner.
                pt: { xs: '88px', sm: '84px', md: '88px' },
                minHeight: { sm: 190, md: 236 },
                boxShadow: `4px 4px 6px -1px ${glow}CC, 0 8px 20px rgba(0, 0, 0, 0.3)`,
                overflow: 'hidden',
                transition: 'box-shadow 0.25s ease',
                // Light streak that sweeps across on hover (parked off the left edge).
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-60%',
                  width: '35%',
                  height: '100%',
                  background:
                    'linear-gradient(105deg, transparent, rgba(255, 255, 255, 0.65), transparent)',
                  transform: 'skewX(-20deg)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: fonts.heading,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  lineHeight: 1.3,
                  color: colors.sponsorHeading,
                  mb: 0.75,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: fonts.mono,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  lineHeight: 1.4,
                  color: colors.sponsorHeading,
                  mb: 1.5,
                }}
              >
                {item.time}
              </Typography>
              {item.location && (
                <Typography
                  sx={{
                    fontFamily: fonts.ui,
                    fontSize: '0.8rem',
                    color: colors.sponsorHeading,
                    lineHeight: 1.4,
                  }}
                >
                  {item.location}
                </Typography>
              )}
            </Box>
            </Box>
          </Box>
          );
        })}

        {/* Decorative seashells at the centre of the ring of cards. Same 5s loop, half a
            cycle apart (the -2.5s delay), so one rises exactly as the other sinks. */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: { xs: 'none', sm: 'flex' },
            alignItems: day.staggerShells ? 'flex-start' : 'center',
            gap: 3,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Box
            component="img"
            src={seashell}
            alt=""
            sx={{ width: 'min(80px, 7vw)', height: 'auto', transform: 'rotate(-25deg)', ...drift(5, 0) }}
          />
          <Box
            component="img"
            src={seashell}
            alt=""
            sx={{
              width: 'min(120px, 10vw)',
              height: 'auto',
              mt: day.staggerShells ? 'min(70px, 6vw)' : 0,
              transform: 'rotate(25deg)',
              ...drift(5, -2.5),
            }}
          />
        </Box>
      </Box>

      {dayIndex === 0 ? (
        <>
        {/* Decorative seashells + starfish, bottom right. Anchored `top: calc(100% + Npx)`
            off the grid's bottom edge (see the wrapper above) rather than `bottom`, so they
            track however tall the grid of cards ends up being. */}
        {/* Top shell: the PNG has lots of transparent padding, so crop to the shell's
            own bounds (159x163 of the 586x431 canvas) — that way width is the visible
            shell width and the rotation pivots around the shell, not the canvas. */}
        <Box
          sx={{
            position: 'absolute',
            right: '2%',
            // Below the cards by default; beside the bottom row once the margin is wide enough
            // to hold it without touching them.
            top: 'calc(100% + 20px)',
            '@media (min-width: 1400px)': { top: 'calc(100% - 150px)' },
            width: 'min(112px, 6.2vw)',
            aspectRatio: '159 / 163',
            overflow: 'hidden',
            transform: 'rotate(-90deg)',
            display: { xs: 'none', sm: 'block' },
            ...drift(6, 0.4),
          }}
        >
          <Box
            component="img"
            src={seashellOutline}
            alt=""
            sx={{
              position: 'absolute',
              width: `${(586 / 159) * 100}%`,
              left: `${(-357 / 159) * 100}%`,
              top: `${(-175 / 163) * 100}%`,
              maxWidth: 'none',
            }}
          />
        </Box>
        {/* Second shell, cropped to the shell's own bounds like the one above. */}
        <Box
          sx={{
            position: 'absolute',
            right: 'calc(18.5% + min(27.5px, 2vw) - min(22.5px, 1.75vw) - 14px)',
            top: 'calc(100% + 68px)',
            width: 'min(45px, 3.5vw)',
            aspectRatio: '159 / 163',
            overflow: 'hidden',
            display: { xs: 'none', sm: 'block' },
            ...drift(5, 1.5),
          }}
        >
          <Box
            component="img"
            src={seashellOutline}
            alt=""
            sx={{
              position: 'absolute',
              width: `${(586 / 159) * 100}%`,
              left: `${(-357 / 159) * 100}%`,
              top: `${(-175 / 163) * 100}%`,
              maxWidth: 'none',
            }}
          />
        </Box>
        <Box
          component="img"
          src={starfishOutline}
          alt=""
          sx={{
            position: 'absolute',
            // ~150px of visible starfish (the PNG has padding, so box = 150 / 0.718),
            // flipped horizontally.
            right: 'calc(3.5% + min(76px, 5.65vw) - min(104.5px, 8.15vw) + 24px)',
            top: 'calc(100% + 110px)',
            width: 'min(209px, 16.3vw)',
            height: 'auto',
            transform: 'scaleX(-1)',
            display: { xs: 'none', sm: 'block' },
            ...sway(9, 0),
          }}
        />
        </>
      ) : (
        // Last day: a starfish resting bottom-left, below the ring.
        <Box
          component="img"
          src={starfishOutline}
          alt=""
          sx={{
            position: 'absolute',
            left: '-1%',
            top: 'calc(100% + 10px)',
            width: 'min(209px, 16.3vw)',
            height: 'auto',
            transform: 'scaleX(-1)',
            display: { xs: 'none', sm: 'block' },
            ...sway(9, 2),
          }}
        />
      )}
      </Box>
        </Fragment>
        );
      })}
    </Box>

    {/* Schedule -> FAQ transition: the same bubble-field treatment as the FAQ -> Sponsors one
        below, with an explicit colour hand-off (the blue Schedule ends on -> FAQ's periwinkle) instead
        of relying on the page's global background gradient to be the right colour here. Eases
        into blue over a longer stretch (less sudden) while still resolving to solid blue well
        before the bottom, so the strip reads as mostly blue overall. */}
    <Box sx={{ position: 'relative', height: SCHEDULE_FAQ_HEIGHT }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          background: {
            xs: '#88A3EA',
            md: `linear-gradient(180deg, ${SCHEDULE_END_BLUE} 0%, #88A3EA 55%, #88A3EA 100%)`,
          },
        }}
      />
      <Box
        ref={scheduleFaqBubblesRef}
        aria-hidden
        // High enough to clear everything nearby (Schedule's own decorations go up to zIndex
        // 15) so the bubbles always render on top of the purple, not behind it.
        sx={{ position: 'absolute', inset: 0, zIndex: 20, pointerEvents: 'none', transform: { xs: 'translateY(45%)', md: 'none' } }}
      >
        {scheduleFaqBubbles.map((b, i) => (
          <Box
            key={i}
            component="img"
            src={bubble}
            alt=""
            className="schedule-faq-bubble"
            sx={{
              position: 'absolute',
              left: `${b.left}%`,
              top: `${b.top}%`,
              width: b.size,
              height: b.size,
              transform: 'translate(-50%, -50%)',
              // Phones get a tenth of the bubbles; the rest stay hidden.
              display: { xs: i % 10 === 0 ? 'block' : 'none', md: 'block' },
              '@media (prefers-reduced-motion: reduce)': { display: 'none' },
            }}
          />
        ))}
      </Box>
    </Box>
    </>
    )}

    <FAQ />

    {/* Sponsors heading + seabed share one wrapper so Triton's sub can swim through both.
        Backed by a flat #4F5BDD (the same blue the transition strip below gradients into),
        so there's no seam once the strip's own gradient ends. */}
    <Box ref={swimRef} sx={{ position: 'relative', mt: '-2px', mb: '-6vw', backgroundColor: '#4F5BDD' }}>
      {/* The colour hand-off from FAQ's periwinkle wash (#8FA6E8) to the sponsors blue
          (#4F5BDD) — explicit and local, rather than relying on the page's global background
          gradient to happen to be the right colour at this scroll position. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: TRANSITION_HEIGHT,
          zIndex: 0,
          background: 'linear-gradient(180deg, #8FA6E8 0%, #4F5BDD 100%)',
        }}
      />

      {/* Rising bubbles marking the transition: each one rises and sways as this strip scrolls
          through the viewport (see the scroll effect above) rather than animating on a loop.
          Each fades in/out at its own point along the scroll (randomized per bubble) instead of
          a shared edge, and isn't clipped to the strip — some sit above 0%/below 100% so they
          visibly drift into the FAQ section above and the sponsors heading below. Sits above
          Triton (zIndex 2 > his 1) so he swims up from behind the bubble field. */}
      <Box
        ref={transitionBubblesRef}
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: TRANSITION_HEIGHT,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        {transitionBubbles.map((b, i) => (
          <Box
            key={i}
            component="img"
            src={bubble}
            alt=""
            className="transition-bubble"
            sx={{
              position: 'absolute',
              left: `${b.left}%`,
              top: `${b.top}%`,
              width: b.size,
              height: b.size,
              transform: 'translate(-50%, -50%)',
              // Phones get a tenth of the bubbles; the rest stay hidden.
              display: { xs: i % 10 === 0 ? 'block' : 'none', md: 'block' },
              '@media (prefers-reduced-motion: reduce)': { display: 'none' },
            }}
          />
        ))}
      </Box>

      {/* Sea life, layered around Triton (see `critters`). The outer box is moved by the scroll
          effect; the inner img bobs on a slow CSS sway. */}
      {critters.map((c, i) => (
        <Box
          key={i}
          className="sponsor-critter"
          data-dx={c.dx}
          data-dy={c.dy}
          aria-hidden
          sx={{
            position: 'absolute',
            left: c.left,
            top: c.top,
            width: { xs: `${c.w * 1.8}vw`, md: `${c.w}vw` },
            '--dx': `${c.dx}vw`,
            '--dy': `${c.dy}vw`,
            animation: {
              xs: `critterTravel ${12 + (i % 4) * 3}s ease-in-out ${c.delay}s infinite alternate`,
              md: 'none',
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            zIndex: c.z,
            pointerEvents: 'none',
          }}
        >
          <Box
            component="img"
            src={c.src}
            alt=""
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              ...(c.bob
                ? {
                    animation: `critterBob ${c.sway}s ease-in-out ${c.delay}s infinite`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }
                : sway(c.sway, c.delay)),
            }}
          />
        </Box>
      ))}

      <Box
        component="img"
        ref={subRef}
        src={tritonInSub}
        alt=""
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: { xs: '30vw', md: '20vw' },
          height: 'auto',
          // Above the heading's background but below the seabed (so he passes behind the coral)
          // and below the bubble field (zIndex 2), so he surfaces from behind the bubbles.
          zIndex: 1,
          opacity: 0,
          pointerEvents: 'none',
          willChange: 'transform',
          '@media (prefers-reduced-motion: reduce)': { display: 'none' },
        }}
      />

      <Box
        ref={sponsorsRef}
        component="section"
        id="sponsors"
        sx={{
          position: 'relative',
          textAlign: 'center',
          px: 2,
          pt: TRANSITION_HEIGHT,
          pb: { xs: '16vw', md: '11vw' },
        }}
      >
        <Typography
          sx={{
            position: 'relative',
            zIndex: 2, // the sub swims behind the heading text
            fontFamily: fonts.display,
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '3.25rem', md: '4.5rem' },
            color: colors.gold,
            textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
            mt: { xs: 3, md: 5 },
          }}
        >
          Thank You To Our Sponsors
        </Typography>
      </Box>

      {/* Seabed: scaled slightly past the viewport so its wavy side edges are cropped off, and
          pulled down so its bottom edge overlaps the top of the footer. */}
      <Box
        ref={reefRef}
        sx={{ position: 'relative', zIndex: 2, overflow: 'hidden', mt: { xs: 2, md: 4 }, pointerEvents: 'none' }}
      >
        <Box
          component="img"
          src={sponsorBg}
          alt=""
          onLoad={() => ScrollTrigger.refresh()}
          sx={{ display: 'block', width: '114%', maxWidth: 'none', ml: '-7%', height: 'auto' }}
        />
      </Box>

      {/* Sponsor shells, piled on the seabed floor. Above everything (zIndex 5): the animals,
          the heading, the seabed picture and the footer overlap. */}
      {sponsorShells.map((sh, i) => (
        <Box
          key={sh.name}
          className="sponsor-shell"
          data-rot={sh.rot}
          sx={{
            position: 'absolute',
            left: { xs: sh.left[0], md: sh.left[1] },
            bottom: { xs: `${sh.bottom[0]}vw`, md: `${sh.bottom[1]}vw` },
            width: { xs: '22vw', md: '16vw' },
            zIndex: 5,
            pointerEvents: 'none',
            transform: `rotate(${sh.rot}deg)`,
          }}
        >
          <Box
            component="img"
            src={seashell}
            alt=""
            sx={{ display: 'block', width: '100%', height: 'auto', filter: `${sh.tint} drop-shadow(0 6px 8px rgba(0,0,0,0.35))` }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: '22%',
              right: '22%',
              top: '24%',
              bottom: '32%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={sh.logo}
              alt={sh.name}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', transform: `scale(${sh.logoScale ?? 1})` }}
            />
          </Box>
        </Box>
      ))}
    </Box>
    </>
  );
};

export default Home;
